import {
  getCustomScheduleTemplates,
  getDayNameForDate,
  parseScheduleTimeOnDate,
  resolveScheduleForDate,
} from "utils/scheduleResolver";
import { getContrastTextColor, normalizeLabelColor } from "utils/scheduleTimelineUtils";
import { isGettingStartedHidden } from "utils/gettingStartedClass";

export const DEFAULT_SIDEBAR_CLASS_MENU_SETTINGS = Object.freeze({
  groupByTodaysSchedule: false,
  showTimes: false,
});

export const NEXT_CLASS_QUICK_JUMP_WINDOW_MS = 20 * 60 * 1000;

const coerceDate = (value) => {
  if (value == null) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

export const formatSidebarPeriodLabel = (period, index) => {
  const trimmed = String(period || "").trim();
  if (trimmed) return trimmed;
  return String(index + 1);
};

export const getSidebarPeriodBadgeStyle = (labelColor) => {
  const backgroundColor = normalizeLabelColor(labelColor);
  return {
    backgroundColor,
    border: "1px solid rgba(15, 23, 42, 0.18)",
    color: getContrastTextColor(backgroundColor),
  };
};

export const formatClassTimeRange = (startTime, endTime) => {
  const startParsed = coerceDate(startTime);
  const endParsed = coerceDate(endTime);
  if (!startParsed || !endParsed) return "";

  const startMeridiem = startParsed.getHours() >= 12 ? "PM" : "AM";
  const endMeridiem = endParsed.getHours() >= 12 ? "PM" : "AM";
  const endLabel = endParsed.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  if (startMeridiem === endMeridiem) {
    const startLabel = startParsed
      .toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
      .replace(/\s?(AM|PM)$/i, "")
      .trim();
    return `${startLabel} – ${endLabel}`;
  }

  const startLabel = startParsed.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return `${startLabel} – ${endLabel}`;
};

export const normalizeSidebarClassMenuSettings = (rawSettings) => {
  const source = rawSettings && typeof rawSettings === "object" ? rawSettings : {};

  return {
    groupByTodaysSchedule: source.groupByTodaysSchedule === true,
    showTimes: source.showTimes === true,
  };
};

export const getSidebarClassMenuSettings = (userSettings = {}) =>
  normalizeSidebarClassMenuSettings(userSettings?.sidebarClassMenu);

export const buildScheduleClassPeriods = ({ classes, resolvedSchedule, activeDate }) => {
  const scheduleClasses = Array.isArray(resolvedSchedule?.template?.classes)
    ? resolvedSchedule.template.classes
    : [];

  if (scheduleClasses.length) {
    return scheduleClasses
      .map((scheduleClass, index) => {
        const classItem = classes.find(
          (candidate) => String(candidate?.classId) === String(scheduleClass?.classId)
        );
        if (classItem && !classItem.active) return null;

        const startTime = parseScheduleTimeOnDate(activeDate, scheduleClass.startTime);
        const endTime = parseScheduleTimeOnDate(activeDate, scheduleClass.endTime);

        return {
          classId: scheduleClass.classId,
          className: scheduleClass.name || classItem?.className || "Class",
          period: classItem?.period,
          periodLabel: formatSidebarPeriodLabel(classItem?.period, index),
          showMarquee: classItem?.showMarquee,
          showClock: classItem?.showClock,
          startTime,
          endTime,
        };
      })
      .filter((period) => period?.startTime)
      .sort((left, right) => left.startTime.getTime() - right.startTime.getTime());
  }

  return classes
    .map((classItem, index) => {
      if (!classItem?.active) return null;

      const scheduleEntry = classItem?.schedule?.[resolvedSchedule?.scheduleKey]?.[resolvedSchedule?.dayName];
      if (!scheduleEntry?.active) return null;

      const startTime = parseScheduleTimeOnDate(activeDate, scheduleEntry.startTime);
      const endTime = parseScheduleTimeOnDate(activeDate, scheduleEntry.endTime);

      return {
        classId: classItem.classId,
        className: classItem.className || "Class",
        period: classItem.period,
        labelColor: classItem.labelColor,
        periodLabel: formatSidebarPeriodLabel(classItem.period, index),
        showMarquee: classItem.showMarquee,
        showClock: classItem.showClock,
        startTime,
        endTime,
      };
    })
    .filter((period) => period?.startTime)
    .sort((left, right) => left.startTime.getTime() - right.startTime.getTime());
};

export const getCurrentClassPeriod = ({ periods, now }) => {
  const currentTime = coerceDate(now)?.getTime?.();
  if (!Number.isFinite(currentTime)) return null;

  return periods.find((period) => {
    const startMs = period.startTime?.getTime?.();
    const endMs = period.endTime?.getTime?.();
    if (!Number.isFinite(startMs)) return false;
    if (!Number.isFinite(endMs)) return currentTime >= startMs;
    return currentTime >= startMs && currentTime < endMs;
  }) || null;
};

export const hasCurrentScheduledClassPeriod = ({
  classes = [],
  userSettings = {},
  activeDate,
  now,
} = {}) => {
  const resolvedActiveDate = coerceDate(activeDate) || new Date();
  const resolvedSchedule = resolveScheduleForDate(userSettings, resolvedActiveDate);
  const periods = buildScheduleClassPeriods({
    classes: Array.isArray(classes) ? classes : [],
    resolvedSchedule,
    activeDate: resolvedActiveDate,
  });

  return Boolean(getCurrentClassPeriod({ periods, now }));
};

export const getNextClassPeriod = ({
  periods = [],
  now,
  withinMs = NEXT_CLASS_QUICK_JUMP_WINDOW_MS,
}) => {
  const currentTime = coerceDate(now)?.getTime?.();
  if (!Number.isFinite(currentTime)) return null;

  const windowEnd = currentTime + (Number.isFinite(withinMs) ? withinMs : NEXT_CLASS_QUICK_JUMP_WINDOW_MS);

  return (Array.isArray(periods) ? periods : [])
    .filter((period) => {
      const startMs = period?.startTime?.getTime?.();
      return Number.isFinite(startMs) && startMs > currentTime && startMs <= windowEnd;
    })
    .sort((left, right) => left.startTime.getTime() - right.startTime.getTime())[0] || null;
};

const buildResolvedScheduleForTemplate = (template, activeDate) => ({
  templateId: template.id,
  template,
  scheduleKey: template.scheduleKey,
  dayName: template.dayMode === "mondayOnly" ? "Monday" : getDayNameForDate(activeDate),
  label: template.label,
});

const buildScheduleByClassId = ({ classes, resolvedSchedule, activeDate }) => {
  const scheduleByClassId = new Map();

  buildScheduleClassPeriods({
    classes,
    resolvedSchedule,
    activeDate,
  }).forEach((period) => {
    scheduleByClassId.set(String(period.classId), period);
  });

  return scheduleByClassId;
};

const buildAlternateScheduleByClassId = ({
  classes,
  userSettings,
  activeDate,
  todayTemplateId,
}) => {
  const alternateScheduleByClassId = new Map();
  const normalizedTodayTemplateId = String(todayTemplateId || "");

  getCustomScheduleTemplates(userSettings)
    .filter((template) => String(template.id) !== normalizedTodayTemplateId)
    .forEach((template) => {
      buildScheduleClassPeriods({
        classes,
        resolvedSchedule: buildResolvedScheduleForTemplate(template, activeDate),
        activeDate,
      }).forEach((period) => {
        const classId = String(period.classId);
        if (!alternateScheduleByClassId.has(classId)) {
          alternateScheduleByClassId.set(classId, period);
        }
      });
    });

  return alternateScheduleByClassId;
};

const buildOrderIndexByClassId = (orderOfClasses = []) => {
  const orderIndexByClassId = new Map();
  (Array.isArray(orderOfClasses) ? orderOfClasses : []).forEach((classId, index) => {
    const normalizedId = String(classId || "");
    if (!normalizedId || orderIndexByClassId.has(normalizedId)) return;
    orderIndexByClassId.set(normalizedId, index);
  });
  return orderIndexByClassId;
};

const compareClassEntries = (left, right, orderIndexByClassId = new Map()) => {
  const leftStart = left.startTime?.getTime?.();
  const rightStart = right.startTime?.getTime?.();
  const leftHasStart = Number.isFinite(leftStart);
  const rightHasStart = Number.isFinite(rightStart);

  if (leftHasStart && rightHasStart && leftStart !== rightStart) {
    return leftStart - rightStart;
  }
  if (leftHasStart !== rightHasStart) {
    return leftHasStart ? -1 : 1;
  }

  const leftOrderIndex = orderIndexByClassId.get(String(left.classId || ""));
  const rightOrderIndex = orderIndexByClassId.get(String(right.classId || ""));
  const leftInOrder = Number.isFinite(leftOrderIndex);
  const rightInOrder = Number.isFinite(rightOrderIndex);

  if (leftInOrder && rightInOrder && leftOrderIndex !== rightOrderIndex) {
    return leftOrderIndex - rightOrderIndex;
  }
  if (leftInOrder !== rightInOrder) {
    return leftInOrder ? -1 : 1;
  }

  const periodCompare = String(left.periodLabel || "").localeCompare(
    String(right.periodLabel || ""),
    undefined,
    { numeric: true, sensitivity: "base" }
  );
  if (periodCompare !== 0) return periodCompare;

  return String(left.className || "").localeCompare(
    String(right.className || ""),
    undefined,
    { sensitivity: "base" }
  );
};

export const buildSidebarTodaysClassesModel = ({
  classes = [],
  userSettings = {},
  orderOfClasses = [],
  activeDate,
  now,
}) => {
  const visibleClasses = (Array.isArray(classes) ? classes : [])
    .filter((classItem) => !isGettingStartedHidden(classItem));
  const menuSettings = getSidebarClassMenuSettings(userSettings);
  const resolvedActiveDate = coerceDate(activeDate) || new Date();
  const resolvedSchedule = resolveScheduleForDate(userSettings, resolvedActiveDate);
  const scheduleByClassId = buildScheduleByClassId({
    classes: visibleClasses,
    resolvedSchedule,
    activeDate: resolvedActiveDate,
  });
  const shouldUseAlternateScheduleTimes =
    menuSettings.groupByTodaysSchedule && menuSettings.showTimes;
  const alternateScheduleByClassId = shouldUseAlternateScheduleTimes
    ? buildAlternateScheduleByClassId({
      classes: visibleClasses,
      userSettings,
      activeDate: resolvedActiveDate,
      todayTemplateId: resolvedSchedule?.templateId,
    })
    : new Map();
  const orderIndexByClassId = buildOrderIndexByClassId(orderOfClasses);

  const currentClass = getCurrentClassPeriod({
    periods: Array.from(scheduleByClassId.values()),
    now,
  });

  const classEntries = visibleClasses
    .filter((classItem) => classItem?.active && classItem?.classId)
    .map((classItem, index) => {
      const scheduleInfo = scheduleByClassId.get(String(classItem.classId));
      const alternateScheduleInfo = alternateScheduleByClassId.get(String(classItem.classId));
      const timeSource = scheduleInfo || alternateScheduleInfo || null;
      const startTime = timeSource?.startTime ?? null;
      const endTime = timeSource?.endTime ?? null;
      const classId = String(classItem.classId);

      return {
        classId,
        className: classItem.className || "Class",
        period: classItem.period,
        labelColor: classItem.labelColor,
        periodLabel: formatSidebarPeriodLabel(classItem.period, index),
        showMarquee: classItem.showMarquee,
        showClock: classItem.showClock,
        startTime,
        endTime,
        timeRangeLabel: formatClassTimeRange(startTime, endTime),
        isCurrent: String(currentClass?.classId || "") === classId,
        isScheduledToday: Boolean(scheduleInfo),
      };
    });

  const sortedClassEntries = classEntries
    .slice()
    .sort((left, right) => compareClassEntries(left, right, orderIndexByClassId));

  const todaysEntries = sortedClassEntries.filter((entry) => entry.isScheduledToday);

  const allEntries = sortedClassEntries.filter((entry) => !entry.isScheduledToday);

  const currentClassEntry =
    sortedClassEntries.find((entry) => entry.isCurrent)
    || null;

  const nextClassPeriod = currentClassEntry
    ? null
    : getNextClassPeriod({
      periods: Array.from(scheduleByClassId.values()),
      now,
      withinMs: NEXT_CLASS_QUICK_JUMP_WINDOW_MS,
    });

  const nextClassEntry = nextClassPeriod
    ? sortedClassEntries.find(
      (entry) => String(entry.classId) === String(nextClassPeriod.classId)
    ) || null
    : null;

  const quickJumpEntry = currentClassEntry || nextClassEntry || null;
  const quickJumpKind = currentClassEntry
    ? "current"
    : (nextClassEntry ? "next" : null);

  return {
    menuSettings,
    classEntries: sortedClassEntries,
    todaysEntries,
    allEntries,
    currentClassEntry,
    nextClassEntry,
    quickJumpEntry,
    quickJumpKind,
    emptyMessage: sortedClassEntries.length ? null : "No classes yet.",
    todaysEmptyMessage: todaysEntries.length ? null : "Nothing scheduled today.",
  };
};

export const getNextScheduledClassDateKey = ({
  classes = [],
  userSettings = {},
  fromDate = new Date(),
  maxDaysAhead = 21,
} = {}) => {
  const start = coerceDate(fromDate) || new Date();
  start.setHours(12, 0, 0, 0);

  for (let offset = 1; offset <= maxDaysAhead; offset += 1) {
    const candidate = new Date(start);
    candidate.setDate(start.getDate() + offset);
    const resolvedSchedule = resolveScheduleForDate(userSettings, candidate);
    if (resolvedSchedule?.isNoSchool) continue;

    const periods = buildScheduleClassPeriods({
      classes,
      resolvedSchedule,
      activeDate: candidate,
    });
    if (periods.length > 0) {
      const year = candidate.getFullYear();
      const month = String(candidate.getMonth() + 1).padStart(2, "0");
      const day = String(candidate.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
  }

  return null;
};
